import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, MenuItem, ProSidebarProvider } from "react-pro-sidebar";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";

import CircularProgress from '@mui/material/CircularProgress';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import DataSaverOnIcon from '@mui/icons-material/DataSaverOn';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PostAddIcon from '@mui/icons-material/PostAdd';
import SettingsIcon from '@mui/icons-material/Settings';
import HomeIcon from '@mui/icons-material/Home';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import FeedIcon from '@mui/icons-material/Feed';
import { getReports } from '../../../Controller/Generic/getstatusfile';
import { MenuOptions } from './sidebar-menu-options';
import pixelWidth from 'string-pixel-width';
import '../components/menu-options/options.css';
import './sidebar-menu.css';

const _autoFitColumns = (json, worksheet, header) => {
    const jsonKeys = header || Object.keys(json[0]);

    const objectMaxLength = [];
    jsonKeys.forEach((key) => {
      objectMaxLength.push(
        pixelWidth(key, {
          size: 3,
        })
      );
    });

    json.forEach((data) => {
      jsonKeys.forEach((key, j) => {
        const l = data[key]
          ? pixelWidth(data[key], {
              size: 3,
            })
          : 0;
        objectMaxLength[j] = Math.max(objectMaxLength[j], l);
      });
    });

    return objectMaxLength.map((w) => {
      return { width: w };
    });
  };

export default function Sidebar(props) { 
    const [fileLoading, setFileLoading] = useState(false);
    const location = useLocation();
    const [activeIndex, setActiveIndex] = useState(() => {
        return MenuOptions.findIndex((value) => value.path === location.pathname);
    });
    
    useEffect(() => {
        setActiveIndex(MenuOptions.findIndex((value) => value.path === location.pathname));
    }, [location.pathname]);
    
    const exportReport = async() => {
        setFileLoading(true);
        
        try {
            // Obtener datos para el informe
            let dataReport = await getReports(props.user);

            const worksheet = XLSX.utils.json_to_sheet(dataReport?.data?.respuesta);
            const wscols = _autoFitColumns(dataReport?.data?.respuesta, worksheet);
            worksheet['!cols'] = wscols;

            const workBook = XLSX.utils.book_new();

            // Añadir hoja al workbook
            XLSX.utils.book_append_sheet(workBook, worksheet, "datos");

            // Generar buffer
            const excelBuffer = XLSX.write(workBook, { bookType: 'xlsx', type: "buffer" });

            const binarydata = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" });
            FileSaver.saveAs(binarydata, process.env.REACT_APP_REPORT_NAME);
            setFileLoading(false);
        } catch (e) {
            console.error("Error al exportar el informe:", e);
            setFileLoading(false);
        } 
    };

    return (
        <ProSidebarProvider>
            <div className="sidebar">
                <div className='sidebar-text-main'>
                    <b>Navigation</b>
                </div>
                <Menu>
                    <MenuItem 
                        active={activeIndex === 0} 
                        icon={<HomeIcon />}
                        onClick={() => setActiveIndex(0)}
                        component={<Link to="/Welcome" />}
                    >
                        Home
                    </MenuItem>
                </Menu>
                <div className='sidebar-text'>
                    <b>Options</b>
                </div>
                <Menu>
                    <MenuItem 
                        active={activeIndex === 1} 
                        icon={<DataSaverOnIcon />} 
                        onClick={() => setActiveIndex(1)}
                        component={<Link to="/Measures" />}
                    >
                        Measures
                    </MenuItem>
                    <MenuItem 
                        active={activeIndex === 2} 
                        icon={<CalendarMonthIcon />} 
                        onClick={() => setActiveIndex(2)}
                        component={<Link to="/Period" />}
                    >
                        Period
                    </MenuItem>
                    <MenuItem 
                        active={activeIndex === 3} 
                        icon={<WorkspacesIcon />} 
                        onClick={() => setActiveIndex(3)}
                        component={<Link to="/Space" />}
                    >
                        Space
                    </MenuItem>
                    {props.user?.profile !== process.env.REACT_APP_ROLE_BAS && (
                        <MenuItem 
                            active={activeIndex === 4} 
                            icon={<PostAddIcon />} 
                            onClick={() => setActiveIndex(4)}
                            component={<Link to="/Request" />}
                        >
                            High Platform Request
                        </MenuItem>
                    )}
                    {props.user?.profile === process.env.REACT_APP_ROLE_ADM && (
                        <MenuItem 
                            active={activeIndex === 5} 
                            icon={<SettingsIcon />} 
                            onClick={() => setActiveIndex(5)}
                            component={<Link to="/Maintenance" />}
                        >
                            Maintenance
                        </MenuItem>
                    )}
                </Menu>
                <Menu>
                    <MenuItem 
                        active={activeIndex === 6} 
                        icon={fileLoading ? <CircularProgress size={20} /> : <FeedIcon />} 
                        onClick={() => exportReport()}
                    >
                        Reports
                    </MenuItem>
                </Menu>
                {props.user?.profile === process.env.REACT_APP_ROLE_ADM && (
                    <>
                        <div className='sidebar-text'>
                            <b>Management</b>
                        </div>
                        <Menu>
                            <MenuItem 
                                active={activeIndex === 7} 
                                icon={<ManageAccountsIcon />} 
                                onClick={() => setActiveIndex(7)}
                                component={<Link to="/Management" />}
                            >
                                Management
                            </MenuItem>
                        </Menu>
                    </>
                )}
            </div>
        </ProSidebarProvider>
    );
}
